import { localStorageHyperDashboardTools } from '@utils/tools/localStorageHyperDashboardTools';
import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { get, set } from '../miscellaneous/objectHelpers';

const paths = {
    blind: {
        propPath: 'blindOrDisabled',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneBlindOrDisabled'
    },
    bhp: {
        propPath: 'bhpDenied',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneWithBHPDenial'
    },
    chip: {
        propPath: 'medicaidChipDenial',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneWithMedicaidChipDenial'
    },
    externalTaxFiler: {
        propPath: 'taxHouseholdComposition.dependentOnExternalFiler',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasExternalFilers'
    },
    foster: {
        propPath: 'specialCircumstances.everInFosterCareIndicator',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneEverInFosterCare'
    },
    homeAddress: {
        propPath: 'livesAtOtherAddressIndicator',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneWithAnotherHomeAddress'
    },
    longterm: {
        propPath: 'longTermCare',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneWithLongTermCare'
    },
    mailingAddress: {
        propPath: 'hasAnotherMailingAddress',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneWithAnotherMailingAddress'
    },
    military: {
        propPath: 'citizenshipImmigrationStatus.honorablyDischargedOrActiveDutyMilitaryMemberIndicator',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneHonorablyDischargedOrActiveDutyMilitary'
    },
    returnedFromTour: {
        propPath: 'returnedFromTourOfActiveDuty',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneReturnedFromTour'
    },
    spouseOrDependentOfVeteran: {
        propPath: 'citizenshipImmigrationStatus.militaryFamilyMemberIndicator',
        thCommonParamPath: 'taxHousehold.thCommonParams.militaryFamilyMember'
    },
    nonCustodialParentClaimer: {
        propPath: 'taxHouseholdComposition.claimedByNonCustodialParent',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasNonCustodialParentClaimers'
    },
    otherThanSpouseOrParentClaimer: {
        propPath: 'taxHouseholdComposition.claimedByOtherThanSpouseOrParent',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasOtherThanSpouseOrParentClaimers'
    },
    pregnant: {
        propPath: 'specialCircumstances.pregnantIndicator',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeonePregnant'
    },
    selfIdentification: {
        propPath: 'americanIndianAlaskaNative.americanIndianAlaskaNativeSelfIdentification',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneAIANSelfIdentified'
    },
    student: {
        propPath: 'fullTimeStudent',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasFullTimeStudent'
    },
    taxFiler: {
        propPath: 'taxHouseholdComposition.taxRelationship',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasTaxFilers'
    },
    termination: {
        propPath: 'medicaidTermination',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneWithExpiringMedicaidChipDenial'
    },
    tribeRecognition: {
        propPath: 'americanIndianAlaskaNative.memberOfFederallyRecognizedTribeIndicator',
        thCommonParamPath: 'taxHousehold.thCommonParams.hasSomeoneFromFederallyRecognizedTribe'
    }
};

const syncThCommonProps = (data, propDescription) => {

    // Extend with custom false and null value if necessary
    const {
        customUpdater = false,
        isCommon = false,
        propName,
        trueValue = true
    } = propDescription;

    const propPath = paths[propName].propPath;
    const commonParamPath = paths[propName].thCommonParamPath;

    let updatedData = cloneObject(data);

    const areAllIndividualValuesFalsy = updatedData.taxHousehold.householdMember.every(hhm => get(hhm, propPath) === false);
    const areAllIndividualValuesNulls = updatedData.taxHousehold.householdMember.every(hhm => get(hhm, propPath) === null);
    const atLeastOneIndividualValueTrue = updatedData.taxHousehold.householdMember.some(hhm => get(hhm, propPath) === trueValue);

    const isDataSyncConsolesShown =
        localStorageHyperDashboardTools.getValueByPropName(
            'isDataSyncConsolesShown',
            false as any,
        );

    if (isCommon) {

        updatedData.taxHousehold.householdMember = updatedData.taxHousehold.householdMember.map((hhm, idx) => {

            // customUpdater is customized HHM's update on common prop changes

            if (customUpdater) {
                return customUpdater(hhm, idx);
            }

            let individualValue = get(hhm, propPath);

            const commonValue = get(updatedData, commonParamPath);

            switch (commonValue) {
                case false:
                    individualValue = false;
                    break;
                case true:
                    individualValue = null;
                    break;
            }

            set(hhm, propPath, individualValue);

            return hhm;
        });
    } else {

        let commonValue;

        switch(true) {
            case atLeastOneIndividualValueTrue:
                commonValue = true;
                break;
            case areAllIndividualValuesFalsy:
                commonValue = null;
                break;
            case areAllIndividualValuesNulls:
                commonValue = null;
                break;
            default:
                commonValue = null;
                break;
        }

        set(updatedData, commonParamPath, commonValue);
    }

    isDataSyncConsolesShown
        && console.log(`${ isCommon ? '\x1B[41;93;2mCommon' : '\x1B[44;97;2mIndividual' } value is touched`);

    isDataSyncConsolesShown
        && console.log({
            data,
            updatedData,
            propName,
            paths: paths[propName],
            commonValue: get(updatedData, commonParamPath),
            commonValueInitial: get(data, commonParamPath),
            individualValues: updatedData.taxHousehold.householdMember.map(hhm => get(hhm, propPath)),
            individualValuesInitial: data.taxHousehold.householdMember.map(hhm => get(hhm, propPath)),
            areAllIndividualValuesFalsy,
            areAllIndividualValuesNulls,
            atLeastOneIndividualValueTrue,
            customUpdater: !!customUpdater,
            trueValue
        });

    return updatedData;
};
export const dataSyncingTools = {
    paths,
    syncThCommonProps
};